import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Box, Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import api from "../../../services/api";
import { useIntl } from "../../../contexts/IntlContext";
import { toBase64 } from "../../../utils/toBase64";
import Upload from "../../../components/Upload";
import LoadingButton from "../../../components/ui/LoadingButton";
import ConfirmDialog from "../../../components/ConfirmDialog";

// Mesmo limite do servidor (PUT /app/establishments/:id/thumbnail-logo).
const MAX_THUMBNAIL_LOGO_BYTES = 2_000_000;

type Props = {
  establishmentId: string;
  initialUrl?: string | null;
};

const getApiMessage = (error: unknown): string | undefined =>
  axios.isAxiosError(error) ? error.response?.data?.message : undefined;

export function ThumbnailLogoCard({ establishmentId, initialUrl }: Props) {
  const intl = useIntl();
  const [logoUrl, setLogoUrl] = useState<string | null>(initialUrl ?? null);
  const [saving, setSaving] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  useEffect(() => {
    setLogoUrl(initialUrl ?? null);
  }, [initialUrl]);

  const endpoint = `/app/establishments/${establishmentId}/thumbnail-logo`;

  const handleUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (file.size > MAX_THUMBNAIL_LOGO_BYTES) {
      toast.error(intl.formatMessage({ id: "establishmentForm.thumbnailLogoTooLarge" }));
      return;
    }

    setSaving(true);
    try {
      const data = await toBase64(file);
      await api.put(endpoint, { path: file.name, data });
      // O PUT responde 204: busca a URL assinada do arquivo salvo; se falhar, mostra o local.
      const stored = await api
        .get(`/app/establishments/${establishmentId}`)
        .then((response) => response.data?.thumbnailLogoUrl as string | null | undefined)
        .catch(() => null);
      setLogoUrl(stored || data);
      toast.success(intl.formatMessage({ id: "establishmentForm.thumbnailLogoSaved" }));
    } catch (error: unknown) {
      toast.error(
        getApiMessage(error) ||
          intl.formatMessage({ id: "establishmentForm.thumbnailLogoError" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRejected = () => {
    toast.error(intl.formatMessage({ id: "establishmentForm.thumbnailLogoRejected" }));
  };

  const handleRemove = async () => {
    setConfirmRemoveOpen(false);
    setSaving(true);
    try {
      await api.delete(endpoint);
      setLogoUrl(null);
      toast.success(intl.formatMessage({ id: "establishmentForm.thumbnailLogoRemoved" }));
    } catch (error: unknown) {
      toast.error(
        getApiMessage(error) ||
          intl.formatMessage({ id: "establishmentForm.thumbnailLogoError" }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<ImageIcon />}
        title={intl.formatMessage({ id: "establishmentForm.thumbnailLogoTitle" })}
        subheader={intl.formatMessage({ id: "establishmentForm.thumbnailLogoHint" })}
      />
      <CardContent>
        <Stack spacing={2} alignItems="flex-start">
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={intl.formatMessage({ id: "establishmentForm.thumbnailLogoTitle" })}
              sx={{
                maxWidth: 160,
                maxHeight: 160,
                objectFit: "contain",
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1,
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: "establishmentForm.thumbnailLogoEmpty" })}
            </Typography>
          )}
          <Upload
            title={intl.formatMessage({
              id: logoUrl
                ? "establishmentForm.thumbnailLogoReplace"
                : "establishmentForm.thumbnailLogoUpload",
            })}
            onUpload={handleUpload}
            onRejected={handleRejected}
            disabled={saving}
            maxFiles={1}
          />
          {logoUrl && (
            <LoadingButton
              color="error"
              variant="outlined"
              size="small"
              fullWidth={false}
              loading={saving}
              onClick={() => setConfirmRemoveOpen(true)}
            >
              {intl.formatMessage({ id: "establishmentForm.thumbnailLogoRemove" })}
            </LoadingButton>
          )}
        </Stack>
      </CardContent>

      <ConfirmDialog
        open={confirmRemoveOpen}
        title={intl.formatMessage({ id: "establishmentForm.thumbnailLogoRemove" })}
        message={intl.formatMessage({ id: "establishmentForm.thumbnailLogoConfirmRemove" })}
        confirmLabel={intl.formatMessage({ id: "establishmentForm.thumbnailLogoRemove" })}
        cancelLabel={intl.formatMessage({ id: "common.cancel" })}
        onClose={() => setConfirmRemoveOpen(false)}
        onConfirm={handleRemove}
      />
    </Card>
  );
}
